import { useEffect, useRef, useCallback, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Play, X, ChevronDown, ArrowRight } from "lucide-react";

function ParticleCanvas() {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const particlesRef = useRef([]);

  const createParticles = useCallback((w, h) => {
    const count = Math.min(Math.floor((w * h) / 20000), 55);
    return Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.2,
      vy: (Math.random() - 0.5) * 0.2,
      r: Math.random() * 1.2 + 0.3,
    }));
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    let w = (cvs.width = window.innerWidth);
    let h = (cvs.height = window.innerHeight);
    particlesRef.current = createParticles(w, h);
    const onResize = () => {
      w = cvs.width = window.innerWidth;
      h = cvs.height = window.innerHeight;
      particlesRef.current = createParticles(w, h);
    };
    window.addEventListener("resize", onResize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      const pts = particlesRef.current;
      for (const p of pts) {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(196,150,59,0.4)";
        ctx.fill();
      }
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const d = dx * dx + dy * dy;
          if (d < 20000) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.strokeStyle = `rgba(196,150,59,${0.08 * (1 - Math.sqrt(d) / 141)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      frameRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(frameRef.current); window.removeEventListener("resize", onResize); };
  }, [createParticles]);

  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-10" />;
}

function AnimatedStat({ value, label, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ""));
  const prefix = value.match(/^\$/) ? "$" : "";
  const suffix = value.match(/K$/) ? "K" : value.match(/B/) ? "B+" : value.match(/\+/) ? "+" : "";

  useEffect(() => {
    if (!inView) return;
    const timer = setTimeout(() => {
      let start = 0;
      const duration = 1500;
      const startTime = performance.now();
      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(eased * numericValue));
        if (progress < 1) requestAnimationFrame(animate);
        else setCount(numericValue);
      };
      requestAnimationFrame(animate);
    }, delay);
    return () => clearTimeout(timer);
  }, [inView, numericValue, delay]);

  return (
    <motion.div
      ref={ref}
      className="stat-item text-center sm:text-left group cursor-default"
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: delay / 1000 + 1.2 }}
    >
      <p className="font-['Cormorant_Garamond'] text-3xl md:text-4xl lg:text-5xl text-[#C4963B] font-light leading-none stat-number transition-all duration-300">
        {prefix}{count}{suffix}
      </p>
      <p className="text-[9px] md:text-[10px] uppercase tracking-[0.25em] text-[#E8E6E1]/30 mt-2 font-medium whitespace-nowrap">
        {label}
      </p>
    </motion.div>
  );
}

function HeroContent({ onWatchVideo }) {
  return (
    <>
      {/* Badge */}
      <motion.div
        className="inline-flex items-center gap-3 mb-7 sm:mb-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2 }}
      >
        <span className="w-8 h-px bg-[#C4963B]" />
        <span className="text-[10px] sm:text-xs uppercase tracking-[0.35em] text-[#C4963B] font-semibold">
          Global Chief AI Officer · PwC
        </span>
      </motion.div>

      {/* Headline */}
      <motion.h1
        data-testid="hero-headline"
        className="font-['Cormorant_Garamond'] text-[3.2rem] sm:text-[4.5rem] md:text-[5.5rem] lg:text-[6.5rem] xl:text-[7.5rem] font-light leading-[0.88] tracking-tight mb-7 sm:mb-10"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="block text-[#F9F6F0]">The</span>
        <em className="block text-shimmer not-italic">Intelligence</em>
        <span className="block text-[#F9F6F0]">Age.</span>
      </motion.h1>

      {/* Subheadline */}
      <motion.p
        data-testid="hero-subheadline"
        className="text-sm sm:text-base md:text-lg text-[#E8E6E1]/55 max-w-xl leading-relaxed mb-8 sm:mb-12 font-light"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.65 }}
      >
        Joe Atkinson has spent 33 years preparing PwC for this moment.
        Now, he's helping leaders everywhere write their own AI growth
        story—responsibly, boldly, and with 370,000 professionals as proof.
      </motion.p>

      {/* CTAs */}
      <motion.div
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-5 mb-10 sm:mb-16"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.85 }}
      >
        <motion.button
          data-testid="hero-watch-keynote-btn"
          onClick={onWatchVideo}
          className="group relative flex items-center justify-center gap-3 px-7 sm:px-8 py-4 bg-[#C4963B] text-[#0A0B0E] text-xs sm:text-sm uppercase tracking-[0.18em] font-bold overflow-hidden"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
        >
          <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
          <Play className="relative w-4 h-4 shrink-0" fill="currentColor" />
          <span className="relative">Watch Latest Keynote</span>
        </motion.button>

        <motion.a
          data-testid="hero-read-briefing-link"
          href="#insights"
          className="group flex items-center justify-center sm:justify-start gap-2 px-7 sm:px-0 py-4 text-xs sm:text-sm text-[#E8E6E1]/50 hover:text-[#C4963B] transition-all duration-300 tracking-wide border sm:border-0 border-white/10 hover:border-[#C4963B]/30 sm:hover:border-0"
          whileHover={{ x: 4 }}
        >
          Board Briefing on Responsible AI
          <ArrowRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1" />
        </motion.a>
      </motion.div>

      {/* Stats */}
      <motion.div
        className="flex items-center gap-6 sm:gap-10 pt-8 sm:pt-10 border-t border-white/[0.06]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.0 }}
      >
        <AnimatedStat value="33" label="Years at PwC" delay={100} />
        <div className="w-px h-10 bg-white/[0.07]" />
        <AnimatedStat value="370K" label="Professionals Led" delay={250} />
        <div className="w-px h-10 bg-white/[0.07]" />
        <AnimatedStat value="$1B+" label="AI Investment" delay={400} />
      </motion.div>
    </>
  );
}

export default function HeroSection() {
  const [videoOpen, setVideoOpen] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 14,
        y: (e.clientY / window.innerHeight - 0.5) * 7,
      });
    };
    window.addEventListener("mousemove", handleMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="overflow-hidden"
      style={{ background: "#0A0B0E" }}
    >

      {/* ─────────── DESKTOP HERO (lg+) ─────────── */}
      <div className="relative min-h-screen items-center hidden lg:flex">
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-1/4 left-[10%] w-[500px] h-[500px] bg-[#C4963B]/[0.04] rounded-full blur-[140px]" />
          <div className="absolute bottom-1/4 right-[20%] w-[300px] h-[300px] bg-[#2A7F7A]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C4963B]/20 to-transparent" />
        </div>

        {/* Desktop photo — full bleed right side, seamlessly blended */}
        <div className="absolute inset-0 z-[1]">
          <motion.img
            src="/joe-hero-hq.jpg"
            alt="Joe Atkinson"
            className="absolute right-0 top-0 h-full object-cover object-[center_20%] w-[62%]"
            style={{ x: mousePos.x * -0.4, y: mousePos.y * -0.3 }}
            initial={{ scale: 1.04, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          />
          {/* Diagonal gradient blend from left */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(108deg, #0A0B0E 0%, #0A0B0E 28%, rgba(10,11,14,0.97) 36%, rgba(10,11,14,0.82) 45%, rgba(10,11,14,0.45) 58%, rgba(10,11,14,0.12) 72%, transparent 85%)",
            }}
          />
          {/* Bottom fade */}
          <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[#0A0B0E] via-[#0A0B0E]/60 to-transparent" />
          {/* Top fade */}
          <div className="absolute top-0 left-0 right-0 h-36 bg-gradient-to-b from-[#0A0B0E]/50 to-transparent" />
        </div>

        {/* Particles */}
        <ParticleCanvas />

        {/* Desktop content */}
        <div className="relative z-20 max-w-7xl mx-auto px-12 pt-32 pb-24 w-full">
          <div className="max-w-[54%]">
            <HeroContent onWatchVideo={() => setVideoOpen(true)} />
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 2 }}
        >
          <span className="text-[9px] uppercase tracking-[0.3em] text-[#E8E6E1]/20">Scroll</span>
          <div className="w-px h-10 relative overflow-hidden bg-white/[0.05]">
            <div className="absolute top-0 w-full bg-[#C4963B]/50 animate-scroll-indicator" style={{ height: "40%" }} />
          </div>
        </motion.div>
      </div>

      {/* ─────────── MOBILE HERO (below lg) ─────────── */}
      <div className="lg:hidden min-h-screen flex flex-col bg-[#0A0B0E]">

        {/* ── Zone 1: Photo — fixed height so face is properly framed ── */}
        <div
          className="relative w-full flex-shrink-0 overflow-hidden"
          style={{ height: "52vh" }}
        >
          <motion.img
            src="/joe-hero-hq.jpg"
            alt="Joe Atkinson"
            className="w-full h-full object-cover"
            style={{ objectPosition: "55% 38%" }}
            initial={{ scale: 1.05, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
          />
          {/* Dark header strip so nav is readable over the photo */}
          <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-[#0A0B0E]/70 to-transparent" />
          {/* Side vignettes */}
          <div className="absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-[#0A0B0E]/50 to-transparent" />
          <div className="absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-[#0A0B0E]/50 to-transparent" />
          {/* Seamless bottom fade into the text zone below */}
          <div
            className="absolute inset-x-0 bottom-0"
            style={{
              height: "55%",
              background:
                "linear-gradient(to bottom, transparent 0%, rgba(10,11,14,0.6) 45%, #0A0B0E 100%)",
            }}
          />
          {/* Gold accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C4963B]/30 to-transparent" />
        </div>

        {/* ── Zone 2: Text — on solid dark background, seamless continuation ── */}
        <div className="relative z-10 flex-1 px-5 pt-3 pb-10">
          <HeroContent onWatchVideo={() => setVideoOpen(true)} />
        </div>
      </div>

      {/* ─────────── VIDEO MODAL (shared) ─────────── */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setVideoOpen(false)}
          >
            <motion.div
              className="relative w-full max-w-5xl"
              initial={{ scale: 0.88, opacity: 0, y: 24 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.88, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4 px-1">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C4963B] font-semibold mb-1">Joe Atkinson · Keynote</p>
                  <h3 className="text-white font-['Cormorant_Garamond'] text-xl md:text-2xl">The Intelligence Age: AI at Enterprise Scale</h3>
                </div>
                <button
                  onClick={() => setVideoOpen(false)}
                  className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C4963B] hover:text-[#C4963B] text-white/50 transition-colors shrink-0 ml-4"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="aspect-video bg-black overflow-hidden rounded-sm shadow-2xl shadow-black">
                <iframe
                  src="https://www.youtube.com/embed/vg6el1FiBU0?autoplay=1&rel=0&modestbranding=1"
                  title="Joe Atkinson Keynote"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
